import React, { useEffect, useState } from "react";
import { apis, patch } from "../../Utils/axios";
import { Button, Form, Input, Modal, Select, Toast, Avatar } from "..";
import { TbBook } from "react-icons/tb";
import { COUNTRIES } from "../../Utils/constants";

// TWCP-XXX: Normalise passport dates to DD/MM/YYYY for UI and DDMMYYYY for API
function formatBackendToUiDate(value) {
	if (!value) return "";
	const str = `${value}`.replace(/\D/g, "");

	// New format: DDMMYYYY (numeric)
	if (/^\d{8}$/.test(str)) {
		const dd = str.slice(0, 2);
		const mm = str.slice(2, 4);
		const yyyy = str.slice(4, 8);
		return `${dd}/${mm}/${yyyy}`;
	}

	// Legacy format: MMYY (numeric) – assume day "01" for display
	if (/^\d{4}$/.test(str)) {
		const mm = str.slice(0, 2);
		const yy = str.slice(2, 4);
		const yyyy = 2000 + parseInt(yy, 10);
		return `01/${mm}/${yyyy}`;
	}

	return value;
}

function formatUiToBackendDate(value) {
	if (!value) return "";
	const parts = value.split("/");
	if (parts.length !== 3) return "";
	const [dd, mm, yyyy] = parts;
	return `${dd}${mm}${yyyy}`;
}

function formatDateInput(value) {
	const digits = `${value || ""}`.replace(/\D/g, "").slice(0, 8);
	if (digits.length <= 2) return digits;
	if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
	return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function parseUiDate(value) {
	if (!value) return null;
	const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
	if (!match) return null;
	const dd = parseInt(match[1], 10);
	const mm = parseInt(match[2], 10);
	const yyyy = parseInt(match[3], 10);

	const date = new Date(yyyy, mm - 1, dd);
	if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) {
		return null;
	}
	return date;
}

function validatePassportDates(expiryUi, issueUi) {
	const currentDate = new Date();
	const expireDate = parseUiDate(expiryUi);
	const issueDate = parseUiDate(issueUi);

	if (!expireDate || !issueDate) {
		return false;
	}

	// Issue date cannot be in the future
	if (issueDate > currentDate) {
		return false;
	}

	// Expiry must be after issue and in the future
	if (expireDate <= issueDate || expireDate <= currentDate) {
		return false;
	}

	return true;
}

function EditPassport({ reload = () => {}, photo, identification = {}, userId, guestId }) {
	const { passportNumber, passportIssueDate, passportExpireDate, passportCountry } = identification || {};
	const [Loading, setLoading] = useState(false);
	const [EditPassport, setEditPassport] = useState();
	const [Values, setValues] = useState({
		passportNumber: passportNumber || "",
		passportIssueDate: formatBackendToUiDate(passportIssueDate),
		passportExpireDate: formatBackendToUiDate(passportExpireDate),
		passportCountry,
	});

	useEffect(() => {
		if (passportNumber || passportIssueDate || passportExpireDate || passportCountry) {
			setValues({
				passportNumber: passportNumber || "",
				passportIssueDate: formatBackendToUiDate(passportIssueDate),
				passportExpireDate: formatBackendToUiDate(passportExpireDate),
				passportCountry,
			});
		}
	}, [passportNumber, passportIssueDate, passportExpireDate, passportCountry]);

	const onUpdate = () => {
		if (!Values.passportNumber || !Values.passportIssueDate || !Values.passportExpireDate) {
			return Toast.error("All the number fields are required");
		}
		if (!Values.passportCountry) {
			return Toast.error("Please select passport country");
		}

		if (!validatePassportDates(Values.passportExpireDate, Values.passportIssueDate)) {
			return Toast.error("Please enter valid dates in DD/MM/YYYY format. Issue date must be in the past and expiry date in the future.");
		}

		const payload = {
			...Values,
			passportIssueDate: formatUiToBackendDate(Values.passportIssueDate),
			passportExpireDate: formatUiToBackendDate(Values.passportExpireDate),
			...(guestId ? { guestId } : { userId }),
		};

		setLoading(true);
		patch(
			userId ? apis.updateUserIdentification(userId) : guestId ? apis.updateGuestIdentification(guestId) : apis.updateMyIdentification,
			payload,
			(r, e) => {
				setLoading(false);
				if (r) {
					reload(payload);
					Toast.success(`Passport Updated`);
				} else if (e) {
					Toast.handleError(e);
				}
			}
		);
	};

	const newChanges =
		passportNumber !== Values.passportNumber ||
		formatBackendToUiDate(passportIssueDate) !== Values.passportIssueDate ||
		formatBackendToUiDate(passportExpireDate) !== Values.passportExpireDate ||
		passportCountry !== Values.passportCountry;

	return (
		<>
			<Button onClick={() => setEditPassport(true)} variant="outlined" className="btn-xs">
				<TbBook className="mr-2" />
				{passportNumber || "Add Passport"}
			</Button>
			<Modal open={EditPassport} onClose={() => setEditPassport()} contentClass="w-[95vw] max-w-[42rem] lg:max-w-[52rem] max-h-[90vh] overflow-auto">
				<div className="p-4 sm:p-6 md:p-10">
					<div className="flex flex-col items-center gap-4 sm:gap-6">
						<div className="flex justify-center">
							<Avatar photo={photo} className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32" />
						</div>
						<div className="w-full max-w-[40rem] min-w-0">
							<h1 className="text-xl sm:text-2xl font-bold text-center sm:text-left pr-10">Passport (IN)</h1>
							<Form onSubmit={onUpdate} className="grid grid-cols-1 gap-3 sm:gap-4 mt-4">
								<Input required maxLength={20} value={Values.passportNumber} label="Passport Number" placeholder="Passport Number" onChange={(passportNumber) => setValues((prev) => ({ ...prev, passportNumber }))} />
								<Select btnClass="w-full" options={COUNTRIES} required selected={{ value: Values.passportCountry, label: Values.passportCountry }} label="Country" placeholder="Country" onChange={(country) => setValues((prev) => ({ ...prev, passportCountry: country.value }))} />
								<div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
									<Input
										allowSpecialChars
										maxLength={10}
										required
										value={Values.passportIssueDate}
										label="Issue Date"
										placeholder="DD/MM/YYYY"
										onChange={(passportIssueDate) =>
											setValues((prev) => ({ ...prev, passportIssueDate: formatDateInput(passportIssueDate) }))
										}
									/>
									<Input
										allowSpecialChars
										maxLength={10}
										required
										value={Values.passportExpireDate}
										label="Expire Date"
										placeholder="DD/MM/YYYY"
										onChange={(passportExpireDate) =>
											setValues((prev) => ({ ...prev, passportExpireDate: formatDateInput(passportExpireDate) }))
										}
									/>
								</div>
								{newChanges && (
									<Button loading={Loading} type="submit" className="w-full">
										Save
									</Button>
								)}
							</Form>
							{/* decorative stamp: keep it in normal flow so it never overlaps fields */}
							<div className="hidden sm:flex justify-center sm:justify-end pt-6">
								{/* <img src="https://cdn.travelwings.com/assets/corp/images/placeholders/stamp.png" alt="stamp" className="w-32 pointer-events-none select-none opacity-80" /> */}
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
}

export default EditPassport;
